/**
 * PassiveLadder — "Your investments now cover your Spotify" ladder.
 *
 * Shows each budget category as a rung, ordered cheapest-first.
 * Covered expenses are vibrant; uncovered ones are ghosted.
 * Includes current vs projected comparison and a "next to cover" callout.
 *
 * Self-contained — reads all data from useApp() and passiveEngine.
 */
import { useMemo, useState } from "react";
import { Card } from '../../components/Card';
import { Heading } from '../../components/Heading';
import { Slider } from '../../components/Slider';
import { F, FF } from '../../utils/format';
import { useApp } from '../../hooks/useApp';
import {
  calculateCoverage,
  getCoverageMessage,
  DEFAULT_YIELD,
} from './passiveEngine';

export default function PassiveLadder() {
  const { s, c, proj, up } = useApp();
  const [showProjected, setShowProjected] = useState(false);

  /* Use a state-stored yield or the default */
  const divYield = s.divYield != null ? s.divYield : DEFAULT_YIELD;

  const coverage = useMemo(
    () => calculateCoverage(s, proj, divYield),
    [s, proj, divYield]
  );

  const message = getCoverageMessage(coverage);

  const displayMonthly = showProjected
    ? coverage.projectedMonthly
    : coverage.currentMonthly;

  const displayAnnual = showProjected
    ? coverage.projectedAnnual
    : coverage.currentAnnual;

  const coveredCount = showProjected
    ? coverage.coveredCountProjected
    : coverage.coveredCountNow;

  const overallCoverage = showProjected
    ? coverage.overallCoverageProjected
    : coverage.overallCoverageNow;

  return (
    <Card c={c} delay={0.18}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        marginBottom: 20, flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <Heading ic={null} c={c}>
            <span style={{ fontSize: 17 }}>💸</span> Passive Income Coverage
          </Heading>
        </div>
        {/* Now / Projected toggle */}
        <div style={{
          display: "flex", borderRadius: 10, overflow: "hidden",
          border: `1px solid ${c.border}`,
        }}>
          <ToggleBtn
            active={!showProjected}
            label="Now"
            onClick={() => setShowProjected(false)}
            c={c} s={s}
          />
          <ToggleBtn
            active={showProjected}
            label={`At ${proj[proj.length - 1]?.a || s.maxAge || 40}`}
            onClick={() => setShowProjected(true)}
            c={c} s={s}
          />
        </div>
      </div>

      {/* Income summary */}
      <div style={{
        display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap",
      }}>
        <div style={{
          flex: "1 1 140px", padding: "14px 16px", borderRadius: 12,
          background: s.dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.04)",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: c.muted,
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5,
          }}>
            Monthly Passive Income
          </div>
          <div style={{
            fontSize: 24, fontWeight: 800, color: c.green,
            letterSpacing: "-0.03em",
          }}>
            {FF(Math.round(displayMonthly))}
          </div>
          <div style={{ fontSize: 11, color: c.muted, marginTop: 3 }}>
            {FF(Math.round(displayAnnual))}/yr at {(divYield * 100).toFixed(1)}% yield
          </div>
        </div>
        <div style={{
          flex: "1 1 140px", padding: "14px 16px", borderRadius: 12,
          background: s.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
        }}>
          <div style={{
            fontSize: 10, fontWeight: 600, color: c.muted,
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5,
          }}>
            Expenses Covered
          </div>
          <div style={{
            fontSize: 24, fontWeight: 800,
            color: coveredCount === coverage.totalCategories ? c.green : c.blue,
            letterSpacing: "-0.03em",
          }}>
            {coveredCount}/{coverage.totalCategories}
          </div>
          <div style={{ fontSize: 11, color: c.muted, marginTop: 3 }}>
            {Math.round(overallCoverage * 100)}% of total expenses
          </div>
        </div>
      </div>

      {/* The Ladder */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {coverage.ladder.map((rung, i) => {
          const covered = showProjected ? rung.coveredProjected : rung.coveredNow;
          const progress = showProjected
            ? (coverage.projectedMonthly >= rung.runningTotal ? 1 : Math.min(1, coverage.projectedMonthly / rung.runningTotal))
            : rung.progressNow;
          const isNext = !covered && (i === 0 || (showProjected ? coverage.ladder[i - 1]?.coveredProjected : coverage.ladder[i - 1]?.coveredNow));

          return (
            <div
              key={rung.id}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px", borderRadius: 12,
                background: covered
                  ? (s.dark ? "rgba(48,209,88,0.06)" : "rgba(48,209,88,0.03)")
                  : isNext
                    ? (s.dark ? "rgba(10,132,255,0.06)" : "rgba(10,132,255,0.03)")
                    : "transparent",
                border: `1px solid ${
                  covered ? (s.dark ? "rgba(48,209,88,0.12)" : "rgba(48,209,88,0.08)")
                  : isNext ? (s.dark ? "rgba(10,132,255,0.12)" : "rgba(10,132,255,0.08)")
                  : "transparent"
                }`,
                opacity: covered ? 1 : isNext ? 0.9 : 0.45,
                transition: "all 0.3s ease",
              }}
            >
              {/* Color dot */}
              <div style={{
                width: 10, height: 10, borderRadius: 5,
                background: covered ? c.green : rung.color,
                flexShrink: 0,
                boxShadow: covered ? `0 0 8px ${c.green}40` : "none",
              }} />

              {/* Name + cost */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: covered ? 600 : 500,
                  color: covered ? c.fg : c.muted,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  {rung.name}
                  {covered && (
                    <span style={{ fontSize: 11, color: c.green, fontWeight: 700 }}>✓</span>
                  )}
                  {isNext && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: c.blue,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      padding: "1px 6px", borderRadius: 4,
                      background: `${c.blue}15`,
                    }}>
                      Next
                    </span>
                  )}
                </div>
                {/* Mini progress bar for uncovered */}
                {!covered && (
                  <div style={{
                    height: 3, borderRadius: 2, marginTop: 5,
                    background: s.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", borderRadius: 2,
                      background: isNext ? c.blue : c.muted,
                      width: `${Math.round(progress * 100)}%`,
                      transition: "width 0.6s cubic-bezier(0.25,0.1,0.25,1)",
                    }} />
                  </div>
                )}
              </div>

              {/* Monthly cost */}
              <div style={{
                fontSize: 13, fontWeight: 600, color: covered ? c.green : c.muted,
                fontVariantNumeric: "tabular-nums", flexShrink: 0,
              }}>
                {FF(rung.monthlyCost)}/mo
              </div>
            </div>
          );
        })}
      </div>

      {/* Motivational message */}
      <div style={{
        marginTop: 16, padding: "12px 16px", borderRadius: 12,
        background: s.dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        fontSize: 13, color: c.muted, lineHeight: 1.5, fontWeight: 500,
      }}>
        💡 {message}
      </div>

      {/* Yield slider */}
      <div style={{ marginTop: 16 }}>
        <Slider
          l="Distribution Yield"
          v={divYield}
          fn={(v) => up("divYield", v)}
          min={0.01}
          max={0.06}
          step={0.005}
          f="%"
          c={c}
        />
        <div style={{ fontSize: 11, color: c.muted, marginTop: -12, opacity: 0.6 }}>
          XEQT ~1.8% · XDIV ~4.5% · ZDV ~4% · Adjust to match your portfolio
        </div>
      </div>
    </Card>
  );
}


/* ─── Toggle button for Now / Projected ─── */
function ToggleBtn({ active, label, onClick, c, s }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px", border: "none",
        background: active ? c.blue : "transparent",
        color: active ? "#fff" : c.muted,
        fontSize: 12, fontWeight: 600, cursor: "pointer",
        transition: "all 0.2s ease",
      }}
    >
      {label}
    </button>
  );
}
